// libraries
import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'

// utils
import { fillPlaceholders } from '/react4xp/components/Announcer'

// svg
import { Check, Loading } from '/react4xp/components/Svg/Ux/Ux'

interface SubmitProps {
    id?: string
    label?: string
    disabled?: boolean
    className?: string
    successMessage?: string
    errorMessage?: string
    validationMessage?: {
        one?: string
        many?: string
    }
}

export default function Submit({
    id,
    label,
    disabled,
    className,
    successMessage,
    errorMessage,
    validationMessage
}: SubmitProps) {

    const [isSending, setIsSending] = useState(false)
    const [isSent, setIsSent] = useState(false)
    const [error, setError] = useState(false)

    const formContext = useFormContext()
    const formState = formContext ? formContext.formState : null

    const submitCount = formState ? formState.submitCount : 0

    // Only recompute when a submit happens. The form validates on every change,
    // so deriving this from the error list would rewrite the live region, and
    // re-announce it, on every keystroke after the first failed submit.
    // react-hook-form lists errors in validation order, which reshuffles as
    // fields are re-validated, so order them by their position in the form.
    const invalidFields = useMemo(() => {
        if (!submitCount || !formState) return []

        return Object.keys(formState.errors).sort((a, b) => {
            const fieldA = document.getElementsByName(a)[0]
            const fieldB = document.getElementsByName(b)[0]

            if (!fieldA || !fieldB) return 0

            return fieldA.compareDocumentPosition(fieldB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
        })
    }, [submitCount])

    // The field's own name is an internal key and does not always match the
    // label the user sees, so read the label the control actually carries.
    const fieldLabel = (name: string) => {
        const field = document.getElementsByName(name)[0]

        if (!field) return name

        // A radio is one answer to a group's question, and the question is what
        // the summary should name, not whichever option happens to come first.
        const group = field.closest('[role="radiogroup"]')
        const groupLabel = group && document.getElementById(group.getAttribute('aria-labelledby'))

        if (groupLabel) return groupLabel.textContent.trim().replace(/\s+/g, ' ')

        const label = (field.id && document.querySelector(`label[for="${CSS.escape(field.id)}"]`))
            || field.closest('label')

        if (!label) return name

        const text = label.textContent.replace(/\(required\)/i, '').trim().replace(/\s+/g, ' ')

        return text && text.length <= 40 ? text : name
    }

    const showSummary = !isSent && invalidFields.length > 0


    const focusField = (name: string) => {
        const field = document.getElementsByName(name)[0]

        if (field) {
            field.focus()
            field.scrollIntoView({ block: 'center' })
        }
    }

    // A failed submit leaves a keyboard user on the button with the summary
    // below it, so send them to the first field that needs attention.
    useEffect(() => {
        if (submitCount && invalidFields.length) focusField(invalidFields[0])
    }, [submitCount])

    // display the loading / spinner
    useEffect(() => {
        const isSending = () => {
            setIsSending(true)
            setError(false)
            setIsSent(false)
		}

		document.addEventListener('formSending', isSending)

		return () => {
			document.removeEventListener('formSending', isSending)
		}
    }, [])

    // hide the loading / spinner and display the sent message
    useEffect(() => {
        const isSent = () => {
            setIsSending(false)
            setIsSent(true)
		}

		document.addEventListener('formSent', isSent)

		return () => {
			document.removeEventListener('formSent', isSent)
		}
    }, [])

    // clear everything after 4 seconds (defined inside Form.tsx)
    useEffect(() => {
        const clearIsSent = () => {
            setIsSending(false)
            setIsSent(false)
            setError(false)
		}

		document.addEventListener('clearFormSent', clearIsSent)

		return () => {
			document.removeEventListener('clearFormSent', clearIsSent)
		}
    }, [])

    // hide the loading / spinner and display the error message
    useEffect(() => {
        const hasErrors = () => {
            setIsSending(false)
            setError(true)
		}

		document.addEventListener('formError', hasErrors)

		return () => {
			document.removeEventListener('formError', hasErrors)
		}
    }, [])

    // clear the is sending state
    useEffect(() => {
        const submitReset = () => {
            setIsSending(false)
		}

		document.addEventListener('formResetSubmit', submitReset)

		return () => {
			document.removeEventListener('formResetSubmit', submitReset)
		}
    }, [])

    return (
        <div className={clsx(
            'submit-wrapper',
            className
        )}>

            <button
                id={id || null}
                type='submit'
                className={clsx(
                    'submit-button labels-xl-medium',
                    isSending && 'submit-button--sending',
                    isSent && 'submit-button--sent'
                )}
                disabled={disabled || false}
            >
                {isSent ? (
                    <>

                        <span className='submit-button__label'>
                            Submitted
                        </span>

                        <Check />

                    </>
                ) : (
                    <>

                        <span className='submit-button__label'>
                            {label || 'Submit'}
                        </span>

                        { isSending && (
                            <span className='loading'>
                                <Loading />
                            </span>
                        )}

                    </>
                )}
                
            </button>

            {isSent && (
                <p className='text-md sent-message'>
                    {successMessage || 'Your message has been submitted successfully.'}
                </p>
            )}

            {error && (
                <p className='text-md error-message'>
                    {errorMessage || 'Something went wrong. Please try again.'}
                </p>
            )}

            <p
                role='alert'
                className='labels-sm-regular error-message error-message--summary'
            >

                {showSummary && (
                    // Keyed on the submit so a repeat with the same failures
                    // still replaces the content. Identical text left in place
                    // is not a change, and would not be announced.
                    <span key={submitCount}>

                        {fillPlaceholders(
                            (invalidFields.length === 1
                                ? validationMessage?.one
                                : validationMessage?.many)
                                || `Please correct <<count>> field${invalidFields.length === 1 ? '' : 's'} before submitting:`,
                            { count: invalidFields.length }
                        )}
                        {' '}

                        {invalidFields.map((name, i) => (
                            <React.Fragment key={name}>
                                {i > 0 && ', '}
                                <button type='button' onClick={() => focusField(name)}>
                                    {fieldLabel(name)}
                                </button>
                            </React.Fragment>
                        ))}

                    </span>
                )}

            </p>

        </div>
    )
}